import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Trash2, Moon, Sparkles, Heart, Users, Send, Mail, Paperclip, Check, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_CODE = 'muycomplicado2026';

export default function RSVPAdmin() {
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  // Broadcast state
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastFile, setBroadcastFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [extraEmails, setExtraEmails] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const fetchRsvps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rsvp`);
      setRsvps(res.data);
      setSelectedIds(new Set(res.data.filter(r => r.email && r.email.trim()).map(r => r.id)));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (authed) fetchRsvps();
  }, [authed, fetchRsvps]);

  const handleDelete = async (id) => {
    await axios.delete(`${API}/rsvp/${id}`);
    setRsvps(prev => prev.filter(r => r.id !== id));
    setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (code === ADMIN_CODE) {
      setAuthed(true);
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  const pollBroadcastStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rsvp/broadcast/status`);
      const s = res.data;
      setSendResult({ sent: s.sent, failed: s.failed, total: s.total, running: s.running, done: s.done });
      if (s.done) {
        setSending(false);
        setBroadcastSubject('');
        setBroadcastMessage('');
        setBroadcastFile(null);
      }
      return s.done;
    } catch {
      return true;
    }
  }, []);

  const handleBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastSubject.trim() || !broadcastMessage.trim()) return;
    setSending(true);
    setSendResult(null);
    try {
      const formData = new FormData();
      formData.append('subject', broadcastSubject);
      formData.append('message', broadcastMessage);
      if (broadcastFile) {
        formData.append('attachment', broadcastFile);
      }
      formData.append('selected_ids', [...selectedIds].join(','));
      formData.append('extra_emails', extraEmails);
      await axios.post(`${API}/rsvp/broadcast`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Poll for progress
      pollRef.current = setInterval(async () => {
        const done = await pollBroadcastStatus();
        if (done) { clearInterval(pollRef.current); pollRef.current = null; }
      }, 2000);
    } catch (e) {
      setSendResult({ error: e.response?.data?.detail || 'Något gick fel' });
      setSending(false);
    }
  };

  const emailCount = rsvps.filter(r => r.email && r.email.trim()).length;
  const sleepCount = rsvps.filter(r => r.sleep_over).length;
  const coCreateCount = rsvps.filter(r => r.co_create && r.co_create.trim()).length;
  const rsvpsWithEmail = rsvps.filter(r => r.email && r.email.trim());
  const extraCount = extraEmails.split(',').filter(e => e.trim() && e.includes('@')).length;
  const recipientCount = selectedIds.size + extraCount;

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selectedIds.size === rsvpsWithEmail.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rsvpsWithEmail.map(r => r.id)));
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[var(--mc-bg)] flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          className="glass rounded-2xl p-8 w-full max-w-sm text-center">
          <h1 className="font-heading text-2xl font-bold text-white mb-2 neon-text">RSVP Admin</h1>
          <p className="text-xs text-[var(--mc-text-muted)] font-mono mb-6">Hemlig sida. Ange koden.</p>
          <form onSubmit={handleLogin}>
            <input type="password" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Hemlig kod" autoFocus
              className="w-full bg-transparent border-b-2 border-[var(--mc-cyan)]/30 focus:border-[var(--mc-cyan)] text-white text-sm font-mono py-3 outline-none text-center mb-4" />
            {error && <p className="text-xs text-red-400 font-mono mb-3">Fel kod.</p>}
            <button type="submit" className="w-full py-3 rounded-full bg-[var(--mc-pink)] text-white font-heading text-sm uppercase tracking-wider cursor-pointer">Öppna</button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--mc-bg)] px-4 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white neon-text">RSVP Svar</h1>
          <p className="text-xs font-mono text-[var(--mc-text-muted)] mt-2">Oktober Fest — Hemlig admin-sida</p>
        </motion.div>

        {/* Stats */}
        <div className="flex gap-4 justify-center mb-8 flex-wrap">
          <div className="glass rounded-xl px-5 py-3 text-center">
            <Users className="w-5 h-5 mx-auto mb-1 text-[var(--mc-pink)]" />
            <span className="font-heading text-2xl font-bold text-white">{rsvps.length}</span>
            <p className="text-[10px] font-mono text-[var(--mc-text-muted)]">Gäster</p>
          </div>
          <div className="glass rounded-xl px-5 py-3 text-center">
            <Mail className="w-5 h-5 mx-auto mb-1 text-[var(--mc-cyan)]" />
            <span className="font-heading text-2xl font-bold text-white">{emailCount}</span>
            <p className="text-[10px] font-mono text-[var(--mc-text-muted)]">Med e-post</p>
          </div>
          <div className="glass rounded-xl px-5 py-3 text-center">
            <Moon className="w-5 h-5 mx-auto mb-1 text-[var(--mc-purple)]" />
            <span className="font-heading text-2xl font-bold text-white">{sleepCount}</span>
            <p className="text-[10px] font-mono text-[var(--mc-text-muted)]">Sover över</p>
          </div>
          <div className="glass rounded-xl px-5 py-3 text-center">
            <Sparkles className="w-5 h-5 mx-auto mb-1 text-[#FFD700]" />
            <span className="font-heading text-2xl font-bold text-white">{coCreateCount}</span>
            <p className="text-[10px] font-mono text-[var(--mc-text-muted)]">Co-create</p>
          </div>
        </div>

        {/* Broadcast section */}
        <div className="mb-10">
          <div className="flex justify-center mb-4">
            <button onClick={() => { setShowBroadcast(!showBroadcast); setSendResult(null); }}
              className="flex items-center gap-2 px-6 py-3 rounded-full glass glow-border-pink font-heading text-xs tracking-[0.15em] uppercase text-[var(--mc-pink)] hover:text-white transition-all cursor-pointer">
              {showBroadcast ? <X className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              {showBroadcast ? 'Stäng' : `Skicka utskick (${emailCount} mottagare)`}
            </button>
          </div>

          {showBroadcast && (
            <motion.form onSubmit={handleBroadcast} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-6 sm:p-8 space-y-4">

              {/* Recipient selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs tracking-[0.15em] uppercase text-[var(--mc-text-muted)] font-mono">Mottagare</label>
                  <button type="button" onClick={selectAll}
                    className="text-[10px] font-mono text-[var(--mc-cyan)] hover:text-white transition-colors cursor-pointer">
                    {selectedIds.size === rsvpsWithEmail.length ? 'Avmarkera alla' : 'Välj alla'}
                  </button>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1 mb-2 pr-1" style={{scrollbarWidth: 'thin', scrollbarColor: '#333 transparent'}}>
                  {rsvpsWithEmail.map(r => (
                    <label key={r.id} className="flex items-center gap-3 py-1.5 px-3 rounded-lg hover:bg-white/5 cursor-pointer transition-colors">
                      <input type="checkbox" checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        className="accent-[var(--mc-pink)] w-3.5 h-3.5 cursor-pointer" />
                      <span className="text-sm text-white font-mono">{r.name}</span>
                      <span className="text-[10px] text-[var(--mc-text-muted)] font-mono">{r.email}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] font-mono text-[var(--mc-text-muted)]">
                  {selectedIds.size === rsvpsWithEmail.length ? `Alla ${emailCount} gäster valda` : `${selectedIds.size} av ${emailCount} gäster valda`}
                </p>
              </div>

              {/* Extra manual emails */}
              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-[var(--mc-text-muted)] font-mono mb-2 block">
                  Extra mottagare (valfritt)
                </label>
                <input value={extraEmails} onChange={e => setExtraEmails(e.target.value)}
                  placeholder="email@exempel.se, annan@exempel.se"
                  className="w-full bg-transparent border-b-2 border-[var(--mc-cyan)]/30 focus:border-[var(--mc-cyan)] text-white text-sm font-mono py-3 outline-none" />
                <p className="text-[10px] font-mono text-[var(--mc-text-muted)] mt-1">Kommaseparerade adresser utanför gästlistan</p>
              </div>

              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-[var(--mc-text-muted)] font-mono mb-2 block">Ämne *</label>
                <input value={broadcastSubject} onChange={e => setBroadcastSubject(e.target.value)} required
                  placeholder="t.ex. Info inför Oktober Fest!"
                  className="w-full bg-transparent border-b-2 border-[var(--mc-cyan)]/30 focus:border-[var(--mc-cyan)] text-white text-sm font-mono py-3 outline-none" />
              </div>

              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-[var(--mc-text-muted)] font-mono mb-2 block">Meddelande *</label>
                <textarea value={broadcastMessage} onChange={e => setBroadcastMessage(e.target.value)} required
                  placeholder="Skriv ditt meddelande här..." rows={6}
                  className="w-full bg-transparent border-b-2 border-[var(--mc-cyan)]/30 focus:border-[var(--mc-cyan)] text-white text-sm font-mono py-3 outline-none resize-none" />
              </div>

              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-[var(--mc-text-muted)] font-mono mb-2 block">
                  <Paperclip className="w-3 h-3 inline mr-1" /> Bifoga fil (valfritt)
                </label>
                <input type="file" onChange={e => setBroadcastFile(e.target.files[0] || null)} accept=".pdf,.png,.jpg,.jpeg"
                  className="w-full text-sm text-[var(--mc-text-muted)] font-mono file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0
                             file:text-xs file:font-mono file:bg-[var(--mc-purple)]/20 file:text-[var(--mc-purple)] file:cursor-pointer" />
                {broadcastFile && (
                  <div className="flex items-center gap-2 mt-2 text-xs font-mono text-[var(--mc-text-sec)]">
                    <Paperclip className="w-3 h-3" /> {broadcastFile.name}
                    <button type="button" onClick={() => setBroadcastFile(null)} className="text-red-400 cursor-pointer">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-2">
                <span className="text-xs font-mono text-[var(--mc-text-muted)]">
                  Skickas till {recipientCount} mottagare
                </span>
                <button type="submit" disabled={sending || recipientCount === 0}
                  className="flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--mc-pink)] text-white font-heading text-xs uppercase tracking-wider cursor-pointer disabled:opacity-40">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? `Skickar${sendResult ? ` (${sendResult.sent}/${sendResult.total})` : '...'}` : 'Skicka utskick'}
                </button>
              </div>

              {sending && sendResult && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="flex items-center gap-2 p-4 rounded-xl bg-[var(--mc-cyan)]/10 border border-[var(--mc-cyan)]/20">
                  <Loader2 className="w-5 h-5 text-[var(--mc-cyan)] animate-spin" />
                  <span className="text-sm text-[var(--mc-cyan)] font-mono">
                    Skickar... {sendResult.sent}/{sendResult.total} skickade
                    {sendResult.failed > 0 && ` (${sendResult.failed} misslyckades)`}
                  </span>
                </motion.div>
              )}
              {sendResult && sendResult.done && !sendResult.error && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="flex items-center gap-2 p-4 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/20">
                  <Check className="w-5 h-5 text-[#39FF14]" />
                  <span className="text-sm text-[#39FF14] font-mono">
                    Skickat till {sendResult.sent} av {sendResult.total} gäster!
                    {sendResult.failed > 0 && ` (${sendResult.failed} misslyckades)`}
                  </span>
                </motion.div>
              )}
              {sendResult?.error && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <span className="text-sm text-red-400 font-mono">{sendResult.error}</span>
                </div>
              )}
            </motion.form>
          )}
        </div>

        {/* RSVP list */}
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--mc-text-muted)]" /></div>
        ) : rsvps.length === 0 ? (
          <p className="text-center text-sm text-[var(--mc-text-muted)] font-mono py-12">Inga RSVP-svar ännu</p>
        ) : (
          <div className="space-y-4">
            {rsvps.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="glass rounded-xl p-5 sm:p-6 group">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <h3 className="font-heading text-lg font-bold text-white">{r.name}</h3>
                      {r.email && <span className="text-xs font-mono text-[var(--mc-text-muted)]">{r.email}</span>}
                      {r.sleep_over && (
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[var(--mc-purple)]/20 text-[var(--mc-purple)] flex items-center gap-1">
                          <Moon className="w-3 h-3" /> Sover över
                        </span>
                      )}
                    </div>
                    {r.co_create && r.co_create.trim() && (
                      <div className="mb-3">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[var(--mc-cyan)] font-mono mb-1">Vill co-create</p>
                        <p className="text-sm text-[var(--mc-text-sec)]">{r.co_create}</p>
                      </div>
                    )}
                    {r.dream && r.dream.trim() && (
                      <div className="mb-3">
                        <p className="text-[10px] tracking-[0.15em] uppercase text-[var(--mc-pink)] font-mono mb-1 flex items-center gap-1">
                          <Heart className="w-3 h-3" /> Dröm
                        </p>
                        <p className="text-sm text-[var(--mc-text-sec)] italic">{r.dream}</p>
                      </div>
                    )}
                    <p className="text-[10px] font-mono text-[var(--mc-text-muted)]">
                      {r.created_at ? new Date(r.created_at).toLocaleString('sv-SE') : ''}
                    </p>
                  </div>
                  <button onClick={() => handleDelete(r.id)}
                    className="opacity-0 group-hover:opacity-100 p-2 hover:bg-red-500/20 rounded transition-all cursor-pointer">
                    <Trash2 className="w-4 h-4 text-red-400/60" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <a href="/" className="text-xs font-mono text-[var(--mc-text-muted)] hover:text-[var(--mc-pink)] transition-colors">
            Tillbaka till sidan
          </a>
        </div>
      </div>
    </div>
  );
}
